import React, { FC, useCallback, useContext, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import classNames from 'classnames';
import { useFormik } from 'formik';
import PageWrapper from '../../../layout/PageWrapper/PageWrapper';
import Page from '../../../layout/Page/Page';
import Card, { CardBody } from '../../../components/bootstrap/Card';
import FormGroup from '../../../components/bootstrap/forms/FormGroup';
import Input from '../../../components/bootstrap/forms/Input';
import useDarkMode from '../../../hooks/useDarkMode';
import AuthContext from '../../../contexts/authContext';
import { useAccessControl } from '../../../contexts/AccessControlContext';
import { validateUsernameAndPassword } from '../../../common/data/ApplicationUserDataHandler';

// ── SVG icons ─────────────────────────────────────────────────────────────────

const EyeIcon: FC = () => (
	<svg width='18' height='18' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round'>
		<path d='M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z' />
		<circle cx='12' cy='12' r='3' />
	</svg>
);

const EyeOffIcon: FC = () => (
	<svg width='18' height='18' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round'>
		<path d='M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94' />
		<path d='M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24' />
		<line x1='1' y1='1' x2='23' y2='23' />
	</svg>
);

const WarnIcon: FC = () => (
	<svg width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2.5' strokeLinecap='round' strokeLinejoin='round'>
		<circle cx='12' cy='12' r='10' />
		<line x1='12' y1='8' x2='12' y2='12' />
		<line x1='12' y1='16' x2='12.01' y2='16' />
	</svg>
);

// ── Component ─────────────────────────────────────────────────────────────────

const Login: FC = () => {
	const { setUser } = useContext(AuthContext);
	const { setPermissionsFromLogin } = useAccessControl();
	const { darkModeStatus } = useDarkMode();

	const [signInPassword, setSignInPassword] = useState<boolean>(false);
	const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
	const [isLoading, setIsLoading] = useState<boolean>(false);
	const [loginError, setLoginError] = useState<string>('');
	const [showPassword, setShowPassword] = useState<boolean>(false);

	const navigate = useNavigate();
	const handleOnClick = useCallback(() => navigate('/analytics-dashboard'), [navigate]);

	const formik = useFormik({
		enableReinitialize: true,
		initialValues: {
			loginEmail: '',
			loginPassword: '',
		},
		validate: (values) => {
			const errors: { loginEmail?: string; loginPassword?: string } = {};
			if (!values.loginEmail) errors.loginEmail = 'Required';
			if (!values.loginPassword) errors.loginPassword = 'Required';
			return errors;
		},
		validateOnChange: false,
		onSubmit: async (values) => {
			setLoginError('');
			setIsSubmitting(true);
			try {
				const userData = await validateUsernameAndPassword(
					values.loginEmail,
					values.loginPassword,
				);
				if (userData) {
					setPermissionsFromLogin(userData.roles || [], userData.permissions || []);
					if (setUser) setUser(values.loginEmail);
					handleOnClick();
				} else {
					setLoginError('Invalid email or password. Please try again.');
					setIsSubmitting(false);
				}
			} catch (error) {
				console.error('Login error:', error);
				setLoginError('An unexpected error occurred. Please try again.');
				setIsSubmitting(false);
			}
		},
	});

	const handleContinue = () => {
		setIsLoading(true);
		setTimeout(() => {
			setSignInPassword(true);
			setIsLoading(false);
		}, 1000);
	};

	const handleNotYou = () => {
		setSignInPassword(false);
		setLoginError('');
		setShowPassword(false);
		formik.setFieldValue('loginPassword', '');
		formik.setErrors({});
	};

	return (
		<PageWrapper
			isProtected={false}
			title='Login'
			className='login-page-wrapper'>
			<Page className='p-0 m-0'>
				<div className={classNames('login-page', { 'login-page--dark': darkModeStatus })}>

					{/* ── Vector geometric background — full coverage ── */}
					<svg className='login-page__bg-svg' viewBox='0 0 1440 900' xmlns='http://www.w3.org/2000/svg' preserveAspectRatio='xMidYMid slice' aria-hidden='true'>

						{/* ── SWEEPING CURVES — cover every vertical zone ── */}
						{/* Zone 1 — top white arcs */}
						<path d='M -200 120 C 200 -80 600 320 1000 80 S 1400 280 1640 120' stroke='rgba(255,255,255,0.05)' strokeWidth='1.2' fill='none'/>
						<path d='M -200 190 C 200 -10 600 390 1000 150 S 1400 350 1640 190' stroke='rgba(255,255,255,0.03)' strokeWidth='0.8' fill='none'/>
						{/* Zone 2 — upper-mid gold arcs */}
						<path d='M -200 320 C 300 100 750 600 1150 280 S 1500 480 1640 320' stroke='rgba(193,140,60,0.06)' strokeWidth='1.2' fill='none'/>
						<path d='M -200 390 C 300 170 750 670 1150 350 S 1500 550 1640 390' stroke='rgba(193,140,60,0.035)' strokeWidth='0.8' fill='none'/>
						{/* Zone 3 — mid white arcs (behind card area) */}
						<path d='M -200 500 C 250 700 700 200 1100 520 S 1450 300 1640 500' stroke='rgba(255,255,255,0.03)' strokeWidth='0.8' fill='none'/>
						{/* Zone 4 — lower-mid gold arcs */}
						<path d='M -200 620 C 300 880 800 340 1150 680 S 1450 420 1640 620' stroke='rgba(193,140,60,0.07)' strokeWidth='1.2' fill='none'/>
						<path d='M -200 690 C 300 950 800 410 1150 750 S 1450 490 1640 690' stroke='rgba(193,140,60,0.04)' strokeWidth='0.8' fill='none'/>
						{/* Zone 5 — bottom white arcs */}
						<path d='M -200 800 C 250 980 720 620 1100 820 S 1450 650 1640 800' stroke='rgba(255,255,255,0.04)' strokeWidth='1' fill='none'/>

						{/* ── RINGS — distributed across full canvas ── */}
						{/* Top-left cluster */}
						<circle cx='110' cy='110' r='105' stroke='rgba(255,255,255,0.05)' strokeWidth='1' fill='none'/>
						<circle cx='110' cy='110' r='66' stroke='rgba(255,255,255,0.035)' strokeWidth='0.7' fill='none'/>
						<circle cx='110' cy='110' r='30' stroke='rgba(193,140,60,0.1)' strokeWidth='0.7' fill='none'/>
						{/* Top-center */}
						<circle cx='720' cy='60' r='55' stroke='rgba(255,255,255,0.04)' strokeWidth='0.8' fill='none'/>
						<circle cx='720' cy='60' r='28' stroke='rgba(193,140,60,0.07)' strokeWidth='0.6' fill='none'/>
						{/* Top-right cluster */}
						<circle cx='1340' cy='120' r='88' stroke='rgba(193,140,60,0.07)' strokeWidth='0.9' fill='none'/>
						<circle cx='1340' cy='120' r='50' stroke='rgba(193,140,60,0.04)' strokeWidth='0.6' fill='none'/>
						{/* Left-middle */}
						<circle cx='55' cy='480' r='70' stroke='rgba(255,255,255,0.04)' strokeWidth='0.8' fill='none'/>
						<circle cx='55' cy='480' r='38' stroke='rgba(193,140,60,0.06)' strokeWidth='0.6' fill='none'/>
						{/* Right-middle */}
						<circle cx='1400' cy='420' r='80' stroke='rgba(255,255,255,0.04)' strokeWidth='0.8' fill='none'/>
						<circle cx='1400' cy='420' r='44' stroke='rgba(193,140,60,0.06)' strokeWidth='0.6' fill='none'/>
						{/* Bottom-left */}
						<circle cx='160' cy='770' r='72' stroke='rgba(255,255,255,0.04)' strokeWidth='0.8' fill='none'/>
						<circle cx='160' cy='770' r='40' stroke='rgba(193,140,60,0.07)' strokeWidth='0.6' fill='none'/>
						{/* Bottom-center */}
						<circle cx='720' cy='870' r='60' stroke='rgba(193,140,60,0.06)' strokeWidth='0.8' fill='none'/>
						<circle cx='720' cy='870' r='32' stroke='rgba(255,255,255,0.04)' strokeWidth='0.6' fill='none'/>
						{/* Bottom-right cluster */}
						<circle cx='1340' cy='800' r='110' stroke='rgba(193,140,60,0.07)' strokeWidth='1' fill='none'/>
						<circle cx='1340' cy='800' r='68' stroke='rgba(193,140,60,0.045)' strokeWidth='0.7' fill='none'/>
						<circle cx='1340' cy='800' r='32' stroke='rgba(255,255,255,0.05)' strokeWidth='0.7' fill='none'/>

						{/* ── DIAGONAL LINES — create a sense of mesh ── */}
						<line x1='0' y1='680' x2='380' y2='0' stroke='rgba(255,255,255,0.022)' strokeWidth='0.7'/>
						<line x1='80' y1='900' x2='520' y2='0' stroke='rgba(255,255,255,0.015)' strokeWidth='0.5'/>
						<line x1='1440' y1='220' x2='1060' y2='900' stroke='rgba(255,255,255,0.022)' strokeWidth='0.7'/>
						<line x1='1360' y1='0' x2='920' y2='900' stroke='rgba(255,255,255,0.015)' strokeWidth='0.5'/>
						<line x1='0' y1='200' x2='600' y2='900' stroke='rgba(193,140,60,0.03)' strokeWidth='0.6'/>
						<line x1='1440' y1='700' x2='840' y2='0' stroke='rgba(193,140,60,0.03)' strokeWidth='0.6'/>

						{/* ── CORNER BRACKETS ── */}
						<path d='M 0 55 L 55 55 L 55 0' stroke='rgba(193,140,60,0.22)' strokeWidth='1.2' fill='none'/>
						<path d='M 1440 55 L 1385 55 L 1385 0' stroke='rgba(255,255,255,0.1)' strokeWidth='1.2' fill='none'/>
						<path d='M 0 845 L 55 845 L 55 900' stroke='rgba(255,255,255,0.08)' strokeWidth='1.2' fill='none'/>
						<path d='M 1440 845 L 1385 845 L 1385 900' stroke='rgba(193,140,60,0.18)' strokeWidth='1.2' fill='none'/>

						{/* ── DIAMONDS — scattered ── */}
						<path d='M 1282 168 L 1310 196 L 1282 224 L 1254 196 Z' stroke='rgba(193,140,60,0.16)' strokeWidth='1' fill='none'/>
						<path d='M 158 720 L 182 744 L 158 768 L 134 744 Z' stroke='rgba(255,255,255,0.08)' strokeWidth='1' fill='none'/>
						<path d='M 318 46 L 336 64 L 318 82 L 300 64 Z' stroke='rgba(255,255,255,0.07)' strokeWidth='0.8' fill='none'/>
						<path d='M 1130 840 L 1148 858 L 1130 876 L 1112 858 Z' stroke='rgba(193,140,60,0.12)' strokeWidth='0.8' fill='none'/>
						<path d='M 420 870 L 435 885 L 420 900 L 405 885 Z' stroke='rgba(255,255,255,0.06)' strokeWidth='0.7' fill='none'/>

						{/* ── ACCENT DOTS — spread throughout ── */}
						<circle cx='48' cy='470' r='2.5' fill='rgba(193,140,60,0.5)'/>
						<circle cx='1400' cy='480' r='2.5' fill='rgba(193,140,60,0.5)'/>
						<circle cx='720' cy='28' r='2' fill='rgba(255,255,255,0.3)'/>
						<circle cx='210' cy='50' r='1.8' fill='rgba(193,140,60,0.45)'/>
						<circle cx='1240' cy='62' r='1.8' fill='rgba(255,255,255,0.25)'/>
						<circle cx='882' cy='872' r='2' fill='rgba(193,140,60,0.4)'/>
						<circle cx='342' cy='872' r='1.8' fill='rgba(255,255,255,0.2)'/>
						<circle cx='48' cy='240' r='1.5' fill='rgba(255,255,255,0.2)'/>
						<circle cx='1395' cy='220' r='1.5' fill='rgba(193,140,60,0.35)'/>
						<circle cx='48' cy='720' r='1.5' fill='rgba(193,140,60,0.35)'/>
						<circle cx='1395' cy='700' r='1.5' fill='rgba(255,255,255,0.2)'/>
						<circle cx='540' cy='38' r='1.5' fill='rgba(193,140,60,0.3)'/>
						<circle cx='920' cy='38' r='1.5' fill='rgba(255,255,255,0.18)'/>
						<circle cx='540' cy='862' r='1.5' fill='rgba(255,255,255,0.18)'/>
						<circle cx='920' cy='862' r='1.5' fill='rgba(193,140,60,0.3)'/>
					</svg>

					{/* Background depth orbs */}
					<div className='login-page__orb login-page__orb--1' />
					<div className='login-page__orb login-page__orb--2' />
					<div className='login-page__orb login-page__orb--3' />

					{/* Single centered column */}
					<div className='login-page__center'>

						{/* Login card */}
						<Card className='login-page__card' data-tour='login-page'>
							<CardBody className='login-page__card-body'>

								{/* Logo + welcome text side by side */}
								<div className='login-page__card-header'>
									<div className='login-page__card-logo'>
										<img
											src={`${process.env.PUBLIC_URL}/HomePageMainLogo.png`}
											alt='JustKnow'
										/>
									</div>
									<div className='login-page__card-intro'>
										<h2 className='login-page__title'>Welcome,</h2>
										<p className='login-page__subtitle'>
											Sign in to continue to your dashboard.
										</p>
									</div>
								</div>

								<form>

									{/* ── STEP 1: Email ── */}
									<div className={classNames('mb-3', { 'd-none': signInPassword })}>
										<FormGroup
											id='loginEmail'
											isFloating
											label='Email or username'
											className='mb-3'>
											<Input
												autoComplete='username'
												value={formik.values.loginEmail}
												isTouched={formik.touched.loginEmail}
												invalidFeedback={formik.errors.loginEmail}
												isValid={formik.isValid}
												onChange={formik.handleChange}
												onBlur={formik.handleBlur}
												onFocus={() => formik.setErrors({})}
											/>
										</FormGroup>

										<button
											type='button'
											className='login-page__btn'
											disabled={!formik.values.loginEmail || isLoading}
											onClick={handleContinue}>
											{isLoading && (
												<span
													className='spinner-border spinner-border-sm'
													role='status'
													aria-hidden='true'
												/>
											)}
											{isLoading ? 'Checking...' : 'Continue'}
										</button>
									</div>

									{/* ── STEP 2: Password ── */}
									<div className={classNames({ 'd-none': !signInPassword })}>

										{/* Email display with "Not you?" */}
										<div className='login-page__email-display mb-3'>
											<span className='login-page__email-display-text'>
												{formik.values.loginEmail}
											</span>
											<button
												type='button'
												className='login-page__email-display-back'
												onClick={handleNotYou}>
												← Not you?
											</button>
										</div>

										{/* Password + eye toggle */}
										<div className='login-page__password-wrapper mb-3'>
											<FormGroup
												id='loginPassword'
												isFloating
												label='Password'>
												<Input
													type={showPassword ? 'text' : 'password'}
													autoComplete='current-password'
													value={formik.values.loginPassword}
													isTouched={formik.touched.loginPassword}
													invalidFeedback={formik.errors.loginPassword}
													isValid={formik.isValid}
													onChange={formik.handleChange}
													onBlur={formik.handleBlur}
													disabled={isSubmitting}
												/>
											</FormGroup>
											<button
												type='button'
												className='login-page__eye-toggle'
												onClick={() => setShowPassword((p) => !p)}
												tabIndex={-1}
												aria-label={showPassword ? 'Hide password' : 'Show password'}>
												{showPassword ? <EyeOffIcon /> : <EyeIcon />}
											</button>
										</div>

										{/* Inline error */}
										{loginError && (
											<div className='login-page__error mb-3'>
												<span className='login-page__error-icon'>
													<WarnIcon />
												</span>
												<div className='login-page__error-body'>
													<span className='login-page__error-label'>
														Authentication failed
													</span>
													<span className='login-page__error-text'>
														{loginError}
													</span>
												</div>
											</div>
										)}

										{/* Sign-in CTA */}
										<button
											type='button'
											className='login-page__btn'
											disabled={isSubmitting}
											onClick={formik.handleSubmit as () => void}>
											{isSubmitting && (
												<span
													className='spinner-border spinner-border-sm'
													role='status'
													aria-hidden='true'
												/>
											)}
											{isSubmitting ? 'Authenticating...' : 'Sign In'}
										</button>
									</div>

								</form>
							</CardBody>
						</Card>

						{/* Footer */}
						<div className='login-page__footer'>
							<a href='/'>Privacy policy</a>
							<a href='/'>Terms of use</a>
						</div>

					</div>
				</div>
			</Page>
		</PageWrapper>
	);
};

export default Login;
